'use client';

import { useState } from 'react';
import { Grid3X3, List, Filter } from 'lucide-react';
import { BookCard } from '@/components/book-card';
import { Header } from '@/components/header';
import { books, categories } from '@/lib/data';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Book } from '@/lib/types';

export default function BooksPage() {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const filteredBooks = books.filter((book) => {
    const matchesSearch = 
      book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.isbn.includes(searchQuery);
    const matchesCategory = selectedCategory === 'all' || book.category === selectedCategory;
    const matchesStatus = selectedStatus === 'all' || book.status === selectedStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const handleEdit = (book: Book) => {
    console.log('Edit book:', book);
  };

  const handleBorrow = (book: Book) => {
    console.log('Borrow book:', book);
  };

  return (
    <div className="min-h-screen bg-[#0A192F]">
      <Header 
        title="图书管理" 
        subtitle={`共 ${filteredBooks.length} 本图书`}
        onSearch={setSearchQuery}
        onAdd={() => setIsAddDialogOpen(true)}
        onScan={() => console.log('Scan')}
      />

      <div className="p-8">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#8892B0]" />
            <span className="text-sm text-[#8892B0]">筛选:</span>
          </div>
          
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-40 bg-[#112240] border-[#1d3557] text-[#E6F1FF]">
              <SelectValue placeholder="选择分类" />
            </SelectTrigger>
            <SelectContent className="bg-[#112240] border-[#1d3557]">
              <SelectItem value="all">全部分类</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-40 bg-[#112240] border-[#1d3557] text-[#E6F1FF]">
              <SelectValue placeholder="选择状态" />
            </SelectTrigger>
            <SelectContent className="bg-[#112240] border-[#1d3557]">
              <SelectItem value="all">全部状态</SelectItem>
              <SelectItem value="available">可借阅</SelectItem>
              <SelectItem value="borrowed">已借出</SelectItem>
              <SelectItem value="reserved">已预约</SelectItem>
              <SelectItem value="maintenance">维护中</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex-1" />

          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-[#112240] rounded-lg p-1 border border-[#1d3557]">
            <Button
              variant="ghost"
              size="icon"
              className={viewMode === 'grid' ? 'bg-[#1d3557] text-[#64FFDA]' : 'text-[#8892B0]'}
              onClick={() => setViewMode('grid')}
            >
              <Grid3X3 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={viewMode === 'list' ? 'bg-[#1d3557] text-[#64FFDA]' : 'text-[#8892B0]'}
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Books Grid/List */}
        <div className={viewMode === 'grid' 
          ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6"
          : "space-y-3"
        }>
          {filteredBooks.map((book) => (
            <BookCard
              key={book.id}
              book={book}
              view={viewMode}
              onEdit={handleEdit}
              onBorrow={handleBorrow}
            />
          ))}
        </div>

        {filteredBooks.length === 0 && (
          <div className="text-center py-16">
            <p className="text-[#8892B0]">没有找到匹配的图书</p>
          </div>
        )}
      </div>

      {/* Add Book Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="bg-[#112240] border-[#1d3557] text-[#E6F1FF] max-w-2xl">
          <DialogHeader>
            <DialogTitle>录入新图书</DialogTitle>
            <DialogDescription className="text-[#8892B0]">
              输入图书信息或扫描ISBN自动获取
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>ISBN</Label>
              <Input className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" placeholder="978-..." />
            </div>
            <div className="space-y-2">
              <Label>书名</Label>
              <Input className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" placeholder="输入书名" />
            </div>
            <div className="space-y-2">
              <Label>作者</Label>
              <Input className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" placeholder="输入作者" />
            </div>
            <div className="space-y-2">
              <Label>出版社</Label>
              <Input className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" placeholder="输入出版社" />
            </div>
            <div className="space-y-2">
              <Label>分类</Label>
              <Select>
                <SelectTrigger className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]">
                  <SelectValue placeholder="选择分类" />
                </SelectTrigger>
                <SelectContent className="bg-[#112240] border-[#1d3557]">
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>存放位置</Label>
              <Input className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" placeholder="如: A区-1排-2层" />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>简介</Label>
              <Input className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" placeholder="输入图书简介" />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" className="border-[#1d3557] text-[#8892B0]" onClick={() => setIsAddDialogOpen(false)}>
              取消
            </Button>
            <Button className="bg-[#64FFDA] text-[#0A192F] hover:bg-[#64FFDA]/90">
              保存
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
